/**
 * Template Registry — frontend single source of truth for the section and
 * element types the JSON renderer understands. Mirrors the backend
 * `template-section-registry.ts`; the two lists must stay in lock-step.
 *
 * The actual element renderers live in `component-registry.tsx` (the runtime
 * `componentRegistry`). This module owns the *type vocabulary* plus the
 * monitoring hook used when the renderer meets a node it doesn't recognise.
 *
 * Graceful-fallback contract (never regress this):
 *   - An unknown section/element type is NEVER surfaced to end users as an
 *     error banner.
 *   - It is logged internally and captured by monitoring (Sentry).
 *   - In development only, a neutral placeholder is shown to help authors.
 */

import { captureFrontendMessage } from '@/src/infrastructure/lib/sentry';

/** Section container types. Keep in sync with backend SUPPORTED_SECTION_TYPES. */
export const SUPPORTED_SECTION_TYPES = [
  'hero',
  'media',
  'details',
  'message',
  'gallery',
  'rsvp',
  'footer',
  'blessing',
  'names',
  'headline',
  'muhurta',
  'venues',
  'ceremonies',
] as const;

/** Element/leaf types. Keep in sync with backend SUPPORTED_ELEMENT_TYPES. */
export const SUPPORTED_ELEMENT_TYPES = [
  'text',
  'image',
  'button',
  'divider',
  'detail-row',
  'blockquote',
  'gallery',
  'rsvp-form',
  'tone-badge',
  'svg-motif',
  'person-block',
  'decorative-headline',
  'muhurta-block',
  'venue-block',
  'ceremony-bar',
  'mandala',
] as const;

export type SectionType = (typeof SUPPORTED_SECTION_TYPES)[number];
export type ElementType = (typeof SUPPORTED_ELEMENT_TYPES)[number];

const SECTION_TYPE_SET: ReadonlySet<string> = new Set(SUPPORTED_SECTION_TYPES);

export function isSupportedSectionType(type: unknown): type is SectionType {
  return typeof type === 'string' && SECTION_TYPE_SET.has(type);
}

/** True in dev/preview builds; drives whether placeholders are visible. */
export function isDevEnvironment(): boolean {
  try {
    return Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
}

// De-dupe noisy logging — we only need to hear about each unknown type once.
const reported = new Set<string>();

/**
 * Record that the renderer encountered a template node it cannot render.
 * Logs to the console and forwards to Sentry as a WARNING message (not an exception — an unknown
 * node is a content/config gap that renders nothing, not a crash, and exceptions are mirrored into
 * GA4's `exception` event). Never throws and never renders anything itself.
 */
export function reportUnknownNode(kind: 'section' | 'element', type: unknown): void {
  // `type` comes from stored template JSON: bound it so a pathological value cannot become a huge
  // Sentry message or an unbounded set entry.
  const typeText = String(type).slice(0, 80);
  const key = `${kind}:${typeText}`;
  if (reported.has(key)) return;
  reported.add(key);

  const message = `Template renderer: no ${kind} renderer registered for "${typeText}"`;
  // eslint-disable-next-line no-console
  console.warn(`[template-registry] ${message}`);
  void captureFrontendMessage(message, 'warning');
}

/** Test-only: reset the de-dupe cache so each test sees a clean slate. */
export function __resetUnknownNodeReportsForTests(): void {
  reported.clear();
}
